"use strict";
/**
 * Polymarket US Market Fetcher
 * Handles fetching market data from Polymarket US API
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseMarketSides = void 0;
exports.polymarketUsEventToDisplayEvent = polymarketUsEventToDisplayEvent;
exports.pmUsMarketCategory = pmUsMarketCategory;
exports.polymarketUsEventMarketToMarket = polymarketUsEventMarketToMarket;
exports.fetchPolymarketUsEvents = fetchPolymarketUsEvents;
exports.fetchPolymarketUsEventMarkets = fetchPolymarketUsEventMarkets;
const state_1 = require("../state");
const polymarket_us_1 = require("../api/polymarket-us");
const cache_1 = require("./cache");
const DEFAULT_MARKET_PRICE = 0.5;
const parseOutcomes = (outcomes) => {
    if (Array.isArray(outcomes)) {
        return outcomes;
    }
    if (typeof outcomes === "string") {
        try {
            const parsed = JSON.parse(outcomes);
            return Array.isArray(parsed) ? parsed : undefined;
        }
        catch (error) {
            const outcomePreview = outcomes;
            console.warn(`Failed to parse PM-US outcomes string as JSON array. Expected format: ["outcome1", "outcome2"]. Outcomes will be undefined. Received: ${outcomePreview}`, error instanceof Error ? error.message : String(error));
        }
    }
    return undefined;
};
const parseMarketSides = (market) => {
    // create an array of side descriptions sorted by long = true first, then by long = false
    if (market?.marketSides && Array.isArray(market.marketSides)) {
        const sortedSides = market.marketSides.sort((a, b) => {
            if (a.long === b.long)
                return 0;
            return a.long ? -1 : 1;
        });
        // Sides without a description are unusable as labels; dropping them
        // lets the caller fall back to Yes/No rather than render blanks.
        return sortedSides
            .map((side) => side.description)
            .filter((description) => Boolean(description));
    }
    return undefined;
};
exports.parseMarketSides = parseMarketSides;
const normalizeEventsResponse = (data) => {
    if (Array.isArray(data)) {
        return data;
    }
    if (data && Array.isArray(data.events)) {
        return data.events;
    }
    return [];
};
function polymarketUsEventToDisplayEvent(event) {
    return {
        id: String(event.id ?? event.slug ?? ""),
        title: String(event.title ?? event.name ?? ""),
        subtitle: event.slug ?? "",
        category: undefined,
        scheduledDate: event.startDate,
    };
}
/**
 * A PM-US market's chip fields. Prefers the fine `sportsMarketType`
 * ("baseball_player_hits") over the coarse `marketType` ("props"), falling
 * back so a market always lands in *some* bucket; the coarse type orders the
 * chips so game lines lead.
 */
function pmUsMarketCategory(market) {
    const fine = String(market.sportsMarketType ?? market.market_type ?? market.marketType ?? "").trim();
    const coarse = String(market.marketType ?? market.market_type ?? "").trim();
    return {
        ...(fine ? { marketCategory: fine } : {}),
        ...(coarse ? { marketCoarseType: coarse } : {}),
    };
}
function polymarketUsEventMarketToMarket(market, eventContext) {
    const bid = market.bestBid ?? DEFAULT_MARKET_PRICE;
    const ask = market.bestAsk ?? DEFAULT_MARKET_PRICE;
    const marketType = market.market_type ?? market.marketType;
    const outcomes = (0, exports.parseMarketSides)(market);
    const [yesSubtitle, noSubtitle] = outcomes && outcomes.length >= 2 ? outcomes : ["Yes", "No"];
    const status = market.closed === true
        ? "finalized"
        : market.active === false
            ? "inactive"
            : "active";
    const subtitleParts = [eventContext?.title, marketType].filter((part) => Boolean(part));
    const subtitle = subtitleParts.join(" • ");
    return {
        id: String(market.slug ?? market.id ?? ""),
        venue: "pm-us",
        // `title` is the market's short label ("Over 6.5 total runs"). This
        // used to prefer `description`, which is the ~500-char resolution
        // paragraph — never a usable title, and no longer sent at all.
        title: String(market.title ?? market.question ?? market.slug ?? ""),
        subtitle,
        category: "pm-us",
        ...pmUsMarketCategory(market),
        strike_price: DEFAULT_MARKET_PRICE,
        status,
        bid: Number.isFinite(bid) ? bid : DEFAULT_MARKET_PRICE,
        ask: Number.isFinite(ask) ? ask : DEFAULT_MARKET_PRICE,
        yesSubtitle,
        noSubtitle,
        outcomes,
        ...(market.minimumTradeQty !== undefined
            ? { minimumTradeQty: market.minimumTradeQty }
            : {}),
    };
}
/**
 * Fetch events from Polymarket US API
 */
async function fetchPolymarketUsEvents(tagSlug) {
    try {
        const resolvedTagSlug = tagSlug || undefined;
        console.log(`Fetching events from Polymarket US API for tag: ${resolvedTagSlug ?? "all"}...`);
        const response = await (0, polymarket_us_1.listPolymarketUsEvents)({
            limit: 200,
            active: true,
            closed: false,
            tagSlug: resolvedTagSlug,
        });
        const events = normalizeEventsResponse(response.data);
        (0, cache_1.setPolymarketUsEventCache)(events);
        const displayEvents = events.map(polymarketUsEventToDisplayEvent);
        console.log(`Fetched ${events.length} Polymarket US events, converted to ${displayEvents.length} display events`);
        return displayEvents;
    }
    catch (error) {
        (0, state_1.updateState)({
            isLoadingMarkets: false,
            errorMessage: "Failed to fetch Polymarket US events.",
        });
        throw error;
    }
}
/**
 * Fetch markets for a single Polymarket US event, on demand.
 *
 * The event list no longer carries nested markets (they were the multi-megabyte
 * part of the payload), so this is a real second call — mirroring how the
 * Kalshi path has always worked. Callers reach this through
 * `fetchEventMarkets()`, which caches the result per event.
 */
async function fetchPolymarketUsEventMarkets(eventId) {
    console.log(`Fetching markets for Polymarket US event: ${eventId}...`);
    // Title comes from the cached list entry when we have it; the response
    // carries the event metadata too, so a cold cache is not an error.
    const cachedEvent = (0, cache_1.getPolymarketUsEventCache)().find((entry) => String(entry.id ?? entry.slug ?? "") === eventId);
    const response = await (0, polymarket_us_1.getPolymarketUsEventMarkets)(eventId);
    const markets = response.data?.markets ?? [];
    const eventTitle = cachedEvent?.title ??
        cachedEvent?.name ??
        response.data?.event?.title ??
        response.data?.event?.name;
    if (markets.length === 0) {
        throw new Error(`No markets found for Polymarket US event: ${eventTitle ?? eventId}`);
    }
    const convertedMarkets = markets.map((market) => polymarketUsEventMarketToMarket(market, { title: eventTitle }));
    console.log(`Fetched ${convertedMarkets.length} markets for Polymarket US event: ${eventTitle ?? eventId}`);
    return convertedMarkets;
}
